import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { EtapaBadge, FINALIDADES, FINALIDADES_FILA, FIN_COR } from "@/components/EtapaOperacao";
import {
  UsersIcon, TruckIcon, ShieldCheckIcon, RadioIcon, CheckCircle2Icon,
  XCircleIcon, MaximizeIcon, ArrowLeftIcon, FootprintsIcon,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

/* ================= TELÃO DA PORTARIA — FULL SCREEN ================= */

const fmtHora = (s) => (s ? new Date(s).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : "—");

function elapsed(horaEntrada, now) {
  if (!horaEntrada) return "—";
  const min = Math.max(0, Math.floor((now - new Date(horaEntrada)) / 60000));
  if (min < 60) return `${min} min`;
  return `${Math.floor(min / 60)}h ${String(min % 60).padStart(2, "0")}m`;
}

export default function PortariaTelao() {
  const [dentro, setDentro] = useState([]);
  const [fila, setFila] = useState([]);
  const [stats, setStats] = useState({ dentro: 0, entradas_hoje: 0, saidas_hoje: 0 });
  const [now, setNow] = useState(new Date());
  const [novos, setNovos] = useState(new Set());
  const [isFull, setIsFull] = useState(false);
  const prevIds = useRef(new Set());
  const navigate = useNavigate();

  // Modo Tela Cheia (Fullscreen API) — ESC sai nativamente
  useEffect(() => {
    const onFs = () => setIsFull(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  const entrarTelaCheia = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    } else {
      document.documentElement.requestFullscreen().catch(() => {});
    }
  };

  const load = async () => {
    try {
      const [d, s, ag] = await Promise.all([
        api.get("/portaria/dentro"),
        api.get("/portaria/stats"),
        api.get("/portaria/aguardando").catch(() => ({ data: [] })),
      ]);
      const ids = new Set(d.data.map((r) => r.id));
      // Destaca quem acabou de entrar
      const recem = new Set([...ids].filter((id) => !prevIds.current.has(id) && prevIds.current.size > 0));
      if (recem.size) {
        setNovos(recem);
        setTimeout(() => setNovos(new Set()), 12000);
      }
      prevIds.current = ids;
      setDentro(d.data);
      setStats(s.data);
      setFila(ag.data);
    } catch { /* mantém último estado */ }
  };

  useEffect(() => {
    load();
    const poll = setInterval(load, 5000);       // atualização automática
    const clock = setInterval(() => setNow(new Date()), 1000);
    return () => { clearInterval(poll); clearInterval(clock); };
  }, []);

  // Motoristas/carretas (com placa) SEMPRE no topo; dentro de cada grupo, mais recente primeiro
  // Só vai para "Carretas na Base" quem ENTROU COM O VEÍCULO (portaria marcou ENTRAR na fila / placa sem fila).
  // Registros antigos sem o campo com_veiculo: decide pela placa.
  const entrouComVeiculo = (r) => (r.com_veiculo ?? !!(r.placa_cavalo || r.placa_carreta));
  const motoristas = useMemo(
    () => dentro.filter(entrouComVeiculo).sort((a, b) => (b.hora_entrada || "").localeCompare(a.hora_entrada || "")),
    [dentro]
  );
  const pedestres = useMemo(
    () => dentro.filter((r) => !entrouComVeiculo(r)).sort((a, b) => (b.hora_entrada || "").localeCompare(a.hora_entrada || "")),
    [dentro]
  );

  const filaPorFinalidade = useMemo(() => {
    const aguardando = fila.filter((f) => f.status === "AGUARDANDO");
    const cols = FINALIDADES_FILA.map((k) => ({
      key: k,
      cor: FIN_COR[k],
      titulo: FINALIDADES[k].replace("DESCARREGAR ", "Descarregar ").replace("CARREGAR ", "Carregar ").replace("CARRETA VAZIA / STANDBY", "Carreta vazia / Standby").replace("CARREGAR LOAD", "Carregar Load"),
      itens: aguardando.filter((f) => (f.finalidades || [f.finalidade]).includes(k)),
    }));
    // Frota que saiu CARREGADA (load) → em trânsito para o destino (PORTO) até voltar à base
    cols.push({ key: "PORTO", cor: "#34C759", titulo: "Saiu p/ PORTO", itens: fila.filter((f) => f.status === "EM_TRANSITO") });
    return cols;
  }, [fila]);

  const FilaCard = ({ f }) => (
    <div className="rounded-xl border-2 border-[#7DD3FC]/70 bg-[#081226] px-4 py-3 shadow-[0_0_18px_rgba(125,211,252,0.12)]"
         data-testid={`telao-fila-${f.id}`}>
      <div className="font-display text-base font-black leading-tight truncate">{f.nome}</div>
      <div className="text-[10px] text-white/40 uppercase truncate">{f.empresa || "—"}</div>
      <div className="flex items-center gap-2 mt-2 flex-wrap">
        {(f.placa_cavalo || f.placa_carreta) && (
          <span className="bg-white text-black font-mono font-black text-xs px-2 py-0.5 border border-black">{f.placa_cavalo || f.placa_carreta}</span>
        )}
        {f.frota && <span className="bg-[#00E5FF] text-black font-mono font-bold text-xs px-2 py-0.5 border border-black">FROTA {f.frota}</span>}
        {f.destino && <span className="bg-[#34C759] text-black font-mono font-bold text-xs px-2 py-0.5 border border-black" data-testid={`telao-fila-destino-${f.id}`}>→ {f.destino}</span>}
        {f.status === "EM_TRANSITO" && <span className="text-[10px] uppercase text-white/50">saiu às</span>}
        <span className="font-mono text-[11px] text-[#34C759]">{fmtHora(f.saiu_em || f.chegou_em)}</span>
        <span className="font-mono text-[11px] text-[#FFD60A]">{elapsed(f.saiu_em || f.chegou_em, now)}</span>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#050B1E] text-white overflow-hidden relative" data-testid="portaria-telao">
      {/* Textura de fundo */}
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none"
           style={{ backgroundImage: "linear-gradient(#00E5FF 1px, transparent 1px), linear-gradient(90deg, #00E5FF 1px, transparent 1px)", backgroundSize: "48px 48px" }} />
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#FFD60A] via-[#00E5FF] to-[#34C759]" />

      {/* HEADER */}
      <header className="relative z-10 flex items-center gap-6 px-8 py-5 border-b border-white/10 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-[#FFD60A] flex items-center justify-center">
            <ShieldCheckIcon className="w-7 h-7 text-black" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-display text-2xl font-black leading-none">PORTARIA <span className="text-[#FFD60A]">AO VIVO</span></div>
            <div className="text-[10px] uppercase tracking-[0.35em] text-[#00E5FF] mt-1 flex items-center gap-2">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#34C759] opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#34C759]" />
              </span>
              Monitoramento em tempo real
            </div>
          </div>
        </div>

        <div className="ml-auto flex items-center gap-8 flex-wrap">
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-widest text-white/40">Entradas hoje</div>
            <div className="font-display text-3xl font-black text-[#00E5FF]" data-testid="telao-entradas">{stats.entradas_hoje}</div>
          </div>
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-widest text-white/40">Saídas hoje</div>
            <div className="font-display text-3xl font-black text-[#FF9F0A]" data-testid="telao-saidas">{stats.saidas_hoje}</div>
          </div>
          <div className="text-center border-l-2 border-[#34C759] pl-8">
            <div className="text-[10px] uppercase tracking-widest text-[#34C759]">Pessoas na base</div>
            <div className="font-display text-6xl font-black text-[#34C759] leading-none" data-testid="telao-contador">{stats.dentro}</div>
          </div>
          <div className="text-right border-l border-white/10 pl-8">
            <div className="font-mono text-4xl font-bold tabular-nums" data-testid="telao-relogio">
              {now.toLocaleTimeString("pt-BR")}
            </div>
            <div className="text-[11px] uppercase tracking-widest text-white/40">
              {now.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" })}
            </div>
          </div>
          <button onClick={entrarTelaCheia} data-testid="telao-fullscreen"
                  className={`flex items-center gap-2 text-[10px] uppercase tracking-widest px-3 py-2 font-bold border transition-colors ${
                    isFull ? "text-white/40 border-white/10 hover:text-white hover:border-white/40" : "bg-[#FFD60A] text-black border-[#FFD60A] hover:bg-[#ffdf3a] animate-pulse"
                  }`}>
            <MaximizeIcon className="w-3.5 h-3.5" /> {isFull ? "Sair (ESC)" : "Tela Cheia"}
          </button>
          <button onClick={() => navigate("/portaria")} data-testid="telao-voltar"
                  className="text-white/40 hover:text-white flex items-center gap-1 text-[10px] uppercase tracking-widest border border-white/10 hover:border-white/40 px-3 py-2">
            <ArrowLeftIcon className="w-3 h-3" /> Operação
          </button>
        </div>
      </header>

      <main className="relative z-10 p-8 flex gap-8 items-start">
        <div className="flex-1 space-y-8 min-w-0">
        {dentro.length === 0 && (
          <div className="flex flex-col items-center justify-center py-32 text-center" data-testid="telao-vazio">
            <UsersIcon className="w-20 h-20 text-white/10 mb-6" />
            <div className="font-display text-4xl font-bold text-white/30">Base sem visitantes no momento</div>
            <div className="text-white/20 mt-2 uppercase tracking-widest text-sm">Aguardando próxima entrada...</div>
          </div>
        )}

        {/* ===== CARRETAS / MOTORISTAS (TOPO) ===== */}
        {motoristas.length > 0 && (
          <section>
            <div className="flex items-center gap-3 mb-4">
              <TruckIcon className="w-6 h-6 text-[#FFD60A]" />
              <h2 className="font-display text-xl font-black uppercase tracking-widest text-[#FFD60A]">
                Carretas na Base <span className="text-white/40 font-mono text-base">({motoristas.length})</span>
              </h2>
              <div className="flex-1 h-px bg-gradient-to-r from-[#FFD60A]/60 to-transparent" />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-4">
              {motoristas.map((r, i) => (
                <div key={r.id} data-testid={`telao-motorista-${r.id}`}
                     className={`relative bg-[#0D1A35] border-l-4 border-l-[#FFD60A] border border-white/10 p-5 transition-all duration-500
                       ${novos.has(r.id) ? "ring-2 ring-[#34C759] shadow-[0_0_40px_rgba(52,199,89,0.35)]" : ""}`}
                     style={{ animation: `telaoIn .5s ease-out ${i * 0.08}s both` }}>
                  {novos.has(r.id) && (
                    <span className="absolute -top-2.5 right-3 bg-[#34C759] text-black text-[10px] font-black uppercase tracking-widest px-2 py-0.5 animate-pulse">Acabou de entrar</span>
                  )}
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-display text-2xl font-black truncate">{r.nome}</div>
                      <div className="text-sm text-white/50 truncate">{r.empresa || "Empresa não informada"}</div>
                    </div>
                    <TruckIcon className="w-8 h-8 text-[#FFD60A]/60 shrink-0" />
                  </div>
                  <div className="flex gap-2 mt-3 flex-wrap">
                    {r.placa_cavalo && (
                      <div className="bg-white text-black font-mono font-black text-lg px-3 py-1 border-2 border-black shadow-[3px_3px_0_#FFD60A]">{r.placa_cavalo}</div>
                    )}
                    {r.placa_carreta && (
                      <div className="bg-white/90 text-black font-mono font-bold text-lg px-3 py-1 border-2 border-black shadow-[3px_3px_0_#00E5FF]">{r.placa_carreta}</div>
                    )}
                    {r.frota && (
                      <div className="bg-[#00E5FF] text-black font-mono font-bold text-lg px-3 py-1 border-2 border-black shadow-[3px_3px_0_#fff]" data-testid={`telao-frota-${r.id}`}>FROTA {r.frota}</div>
                    )}
                  </div>
                  <div className="mt-4 pt-3 border-t border-white/10 text-sm space-y-1.5">
                    <div className="flex items-center gap-4 flex-wrap">
                      {r.chegou_em && <span className="text-white/40 text-xs uppercase" data-testid={`telao-chegada-${r.id}`}>Chegada <b className="text-[#FFD60A] font-mono">{fmtHora(r.chegou_em)}</b></span>}
                      <span className="text-white/40 text-xs uppercase">Entrada <b className="text-[#34C759] font-mono">{fmtHora(r.hora_entrada)}</b></span>
                      <span className="text-white/40 text-xs uppercase">Na base há <b className="text-white font-mono">{elapsed(r.hora_entrada, now)}</b></span>
                    </div>
                    {r.chegou_em && (
                      <div className="text-white/40 text-xs uppercase" data-testid={`telao-tempo-total-${r.id}`}>
                        Tempo total desde a chegada <b className="text-[#FFD60A] font-mono text-sm">{elapsed(r.chegou_em, now)}</b>
                      </div>
                    )}
                    <div className="flex items-center gap-2 flex-wrap">
                      {r.cracha_temporario && (
                        <span className="bg-[#FF9F0A]/15 text-[#FF9F0A] border border-[#FF9F0A]/40 font-mono text-xs font-bold px-2 py-0.5">CRACHÁ {r.cracha_temporario}</span>
                      )}
                      {r.briefing != null && (r.briefing
                        ? <span className="flex items-center gap-1 text-[#34C759] text-xs"><CheckCircle2Icon className="w-3.5 h-3.5" /> Briefing</span>
                        : <span className="flex items-center gap-1 text-[#FF3B30] text-xs"><XCircleIcon className="w-3.5 h-3.5" /> Sem briefing</span>)}
                    </div>
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-3 flex-wrap">
                    <div className="text-[11px] uppercase tracking-widest text-[#00E5FF]/70">{r.motivo_entrada}</div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {r.operacao && <EtapaBadge op={r.operacao} size="lg" testid={`telao-etapa-${r.id}`} />}
                      {(r.operacao?.destino || r.destino) && (
                        <span className="bg-[#34C759] text-black font-mono font-black text-sm px-3 py-1 border-2 border-black shadow-[3px_3px_0_#fff]" data-testid={`telao-destino-${r.id}`}>
                          DESTINO: {r.operacao?.destino || r.destino}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ===== PEDESTRES / VISITANTES ===== */}
        {pedestres.length > 0 && (
          <section>
            <div className="flex items-center gap-3 mb-4">
              <FootprintsIcon className="w-5 h-5 text-[#00E5FF]" />
              <h2 className="font-display text-lg font-black uppercase tracking-widest text-[#00E5FF]">
                Pedestres & Visitantes <span className="text-white/40 font-mono text-base">({pedestres.length})</span>
              </h2>
              <div className="flex-1 h-px bg-gradient-to-r from-[#00E5FF]/50 to-transparent" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
              {pedestres.map((r, i) => (
                <div key={r.id} data-testid={`telao-pedestre-${r.id}`}
                     className={`bg-[#0A1228] border border-white/10 border-l-2 border-l-[#00E5FF] p-4 transition-all duration-500
                       ${novos.has(r.id) ? "ring-2 ring-[#34C759] shadow-[0_0_30px_rgba(52,199,89,0.3)]" : ""}`}
                     style={{ animation: `telaoIn .5s ease-out ${i * 0.06}s both` }}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-display text-lg font-bold truncate">{r.nome}</div>
                    {novos.has(r.id) && <span className="text-[9px] bg-[#34C759] text-black font-black uppercase px-1.5 py-0.5 animate-pulse shrink-0">Novo</span>}
                  </div>
                  <div className="text-xs text-white/40 truncate">{r.empresa || "—"}</div>
                  <div className="flex items-center justify-between mt-3 text-xs flex-wrap gap-1">
                    {r.chegou_em && <span className="font-mono text-[#FFD60A]" title="Chegada" data-testid={`telao-chegada-${r.id}`}>Chegou {fmtHora(r.chegou_em)}</span>}
                    <span className="font-mono text-[#34C759]">{fmtHora(r.hora_entrada)}</span>
                    <span className="font-mono text-white/60">{elapsed(r.hora_entrada, now)}</span>
                    {r.cracha_temporario
                      ? <span className="font-mono text-[#FF9F0A]">#{r.cracha_temporario}</span>
                      : r.chave ? <span className="font-mono text-[#FFD60A]">#{r.chave}</span> : null}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
        </div>

        {/* ===== FILA DE ESPERA (AGUARDANDO ENTRADA) ===== */}
        {fila.length > 0 && (
          <aside className="w-[840px] 2xl:w-[1080px] shrink-0 border-l border-white/10 pl-6 grid grid-cols-6 gap-3" data-testid="telao-fila-espera">
            {filaPorFinalidade.map(({ key, titulo, itens, cor }) => (
              <div key={key}>
                <div className="text-[11px] font-black uppercase tracking-[0.2em] mb-3 flex items-center gap-2" style={{ color: cor }}>
                  <TruckIcon className="w-4 h-4" /> {titulo} <span className="text-white/40 font-mono">({itens.length})</span>
                </div>
                <div className="space-y-3" data-testid={`telao-fila-col-${key}`}>
                  {itens.length === 0 && <div className="rounded-xl border-2 border-dashed border-white/10 px-4 py-5 text-center text-[10px] uppercase tracking-widest text-white/20">Sem veículos</div>}
                  {itens.map((f) => <FilaCard key={f.id} f={f} />)}
                </div>
              </div>
            ))}
          </aside>
        )}
      </main>

      {/* Convite para tela cheia */}
      {!isFull && (
        <button onClick={entrarTelaCheia} data-testid="telao-fullscreen-banner"
                className="fixed bottom-12 left-1/2 -translate-x-1/2 z-20 flex items-center gap-3 bg-[#FFD60A] text-black font-black uppercase tracking-widest text-sm px-6 py-3 shadow-[0_0_40px_rgba(255,214,10,0.35)] hover:bg-[#ffdf3a] transition-colors">
          <MaximizeIcon className="w-5 h-5" strokeWidth={2.5} />
          Clique para Tela Cheia
          <span className="font-normal normal-case tracking-normal text-xs opacity-70">· ESC volta ao normal</span>
        </button>
      )}

      {/* Rodapé */}
      <footer className="fixed bottom-0 left-0 right-0 z-10 bg-black/60 backdrop-blur border-t border-white/10 px-8 py-2 flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-white/30">
        <RadioIcon className="w-3 h-3 text-[#34C759] animate-pulse" />
        CHEM·WMS · Painel atualizado automaticamente a cada 5 segundos
        <span className="ml-auto font-mono normal-case tracking-normal">Última sync: {now.toLocaleTimeString("pt-BR")}</span>
      </footer>

      <style>{`
        @keyframes telaoIn {
          from { opacity: 0; transform: translateY(16px) scale(.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
}
